import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import BrandMark from "./BrandMark";

const navLinks = [
  { type: "section", hash: "#willkommen", label: "Willkommen" },
  { type: "section", hash: "#planner", label: "Planer" },
  { type: "page", to: "/produkte", label: "Produkte" },
  { type: "page", to: "/buecher", label: "Buchhandlung" },
  { type: "section", hash: "#geschichte", label: "Geschichte" },
  { type: "section", hash: "#newsletter", label: "Newsletter" },
  { type: "section", hash: "#kontakt", label: "Kontakt" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleHashNav = (hash) => {
    setOpen(false);
    if (!onHome) {
      navigate(`/${hash}`);
      return;
    }
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handlePageNav = (to) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || !onHome
          ? "bg-cream-50/90 backdrop-blur-xl border-b border-white/60 shadow-[0_14px_45px_-40px_rgba(44,39,36,0.55)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1480px] mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-6">
        <Link
          to="/"
          data-testid="brand-link"
          onClick={() => setOpen(false)}
          className="group inline-flex items-center gap-3 hover:opacity-90 transition-opacity flex-shrink-0 min-w-[190px]"
        >
          <BrandMark variant="icon-only" size="sm" testId="navbar-icon" />
          <BrandMark
            variant="wordmark"
            size="md"
            testId="navbar-wordmark"
            className="whitespace-nowrap"
          />
        </Link>

        <nav className="hidden lg:flex flex-1 items-center justify-center gap-4 xl:gap-6 min-w-0">
          {navLinks.map((l) =>
            l.type === "page" ? (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to.slice(1)}`}
                className={`text-[11px] xl:text-[12px] tracking-[0.18em] xl:tracking-[0.2em] uppercase font-light link-underline whitespace-nowrap ${
                  location.pathname === l.to
                    ? "text-mauve-500"
                    : "text-ink-700 hover:text-ink-900"
                }`}
              >
                {l.label}
              </Link>
            ) : (
              <button
                key={l.hash}
                data-testid={`nav-${l.hash.slice(1)}`}
                onClick={() => handleHashNav(l.hash)}
                className="text-[11px] xl:text-[12px] tracking-[0.18em] xl:tracking-[0.2em] uppercase text-ink-700 hover:text-ink-900 link-underline font-light whitespace-nowrap"
              >
                {l.label}
              </button>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-2 rounded-full border border-ink-700/15 bg-cream-50 px-3 py-1 text-[11px] tracking-[0.18em] uppercase text-ink-700 flex-shrink-0">
          <span className="text-sm">🌐</span>
          <div id="google_translate_element" className="translate-box"></div>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((s) => !s)}
          className="lg:hidden ml-auto text-ink-900"
          aria-label="Menü öffnen"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <div
        data-testid="mobile-menu"
        className={`lg:hidden overflow-hidden transition-[max-height] duration-500 ease-in-out border-t border-cream-300 bg-cream-50/95 backdrop-blur-md ${
          open ? "max-h-[680px]" : "max-h-0"
        }`}
      >
        <div className="flex flex-col px-6 py-4">
          {navLinks.map((l) =>
            l.type === "page" ? (
              <button
                key={l.to}
                data-testid={`mobile-nav-${l.to.slice(1)}`}
                onClick={() => handlePageNav(l.to)}
                className="text-left py-3 text-sm tracking-[0.18em] uppercase text-ink-700"
              >
                {l.label}
              </button>
            ) : (
              <button
                key={l.hash}
                data-testid={`mobile-nav-${l.hash.slice(1)}`}
                onClick={() => handleHashNav(l.hash)}
                className="text-left py-3 text-sm tracking-[0.18em] uppercase text-ink-700"
              >
                {l.label}
              </button>
            )
          )}

          <div className="mt-4 rounded-full border border-ink-700/15 bg-cream-50 px-3 py-2 text-[11px] tracking-[0.18em] uppercase text-ink-700">
            <div id="google_translate_element_mobile" className="translate-box"></div>
          </div>
        </div>
      </div>
    </header>
  );
}
